// Types
import { IPageParam } from "@/Common/api/types/pagination";

// Queries
import { getVehicles } from "@/DataPages/api/inventory/queries";

// Helpers
import { and, or } from "@Common/helpers/sonar";

// Hooks
import { Inventory, IVehicleProperty } from "@/DataPages/types/inventory/models/inventory";
import { useBasicPaginatedResource } from "@/Common/hooks/Pagination/useBasicPaginatedResource";

function toCamelCase(str: string): string {
	str = str.toLowerCase();
	return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match, index) {
		if (+match === 0) {
			return "";
		}
		return index === 0 ? match.toLowerCase() : match.toUpperCase();
	});
}

const getParsedValue = (property: IVehicleProperty) => {
	switch (property.dataType.toUpperCase()) {
		case "BOOLEAN":
			return property.value === "true";
		case "DOLLAR":
			return `$${property.value}`;
		case "FLOAT":
			return parseFloat(property.value).toFixed(property.precision ?? 2);
		default:
			return property.value;
	}
};

const getRowsOfVehicles = (vehicle: Inventory) => {
	return vehicle.properties.reduce(
		(row: Inventory, property: IVehicleProperty) => {
			// @ts-ignore
			row[toCamelCase(property.displayName)] = getParsedValue(property);
			return row;
		},
		{
			_id: vehicle._id,
			dmsId: vehicle.dmsId,
			gpsId: vehicle.gpsId,
			gpsProtected: vehicle.gpsProtected,
			gpsInconsistency: vehicle.gpsInconsistency,
			kmsId: vehicle.kmsId,
			systemStatus: vehicle.systemStatus,
			statusInHistory: vehicle.statusInHistory,
			properties: vehicle.properties,
			keyStates: vehicle.keyStates ?? [],
			kmsLastEventsByKiosk: vehicle.kmsLastEventsByKiosk ?? [],
		} as Inventory
	);
};

const INVENTORY_PAGINATION_SIZE = 500;

interface IUseInventoryPaginatedQueryConfiguration {
	enabled?: boolean;
	commissionCycleId: string;
	payPeriodId: string;
	startDate: Date;
	endDate: Date;
	vehicleCategory: string;
	locationId: string;
}

const validateRequiredParams = (
	locationId: string,
	commissionCycleId: string,
	payPeriodId: string,
	vehicleCategory: string,
	startDate: Date,
	endDate: Date
) => {
	if (or(!locationId, !commissionCycleId, !payPeriodId, !vehicleCategory, !startDate, !endDate)) {
		throw new Error("Cant request vehicles without required parameters");
	}
};

const validatePageParam = (pageParam: unknown) => {
	const typedPageParam = pageParam as IPageParam<string>;
	if (!typedPageParam || !("after" in typedPageParam) || !("first" in typedPageParam)) {
		throw new Error("PageParam should have properties 'after' and 'first'");
	}
	return typedPageParam;
};

const buildVehicleQueryParams = (
	locationId: string,
	commissionCycleId: string,
	payPeriodId: string,
	startDate: Date,
	endDate: Date,
	vehicleCategory: string,
	typedPageParam: IPageParam<string>
) => ({
	locationId,
	commissionCycleId,
	payPeriodId,
	startDate,
	endDate,
	vehicleCategory,
	first: typedPageParam.first,
	after: typedPageParam.after,
});

interface IVehicleQueryParams {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	startDate: Date;
	endDate: Date;
	vehicleCategory: string;
}

const createQueryFunction =
	(params: IVehicleQueryParams) =>
	async ({ pageParam }: { pageParam: unknown }) => {
		const { locationId, commissionCycleId, payPeriodId, vehicleCategory, startDate, endDate } = params;
		validateRequiredParams(locationId, commissionCycleId, payPeriodId, vehicleCategory, startDate, endDate);

		const typedPageParam = validatePageParam(pageParam);

		const queryParams = buildVehicleQueryParams(
			locationId,
			commissionCycleId,
			payPeriodId,
			startDate,
			endDate,
			vehicleCategory,
			typedPageParam
		);

		const result = await getVehicles(queryParams);

		if (!result) {
			throw new Error("Something went wrong while fetching Inventory data");
		}

		return {
			items: result.items.map(getRowsOfVehicles),
			pageInfo: result.pageInfo,
		};
	};

export const useInventoryPaginatedQuery = ({
	enabled = true,
	locationId,
	commissionCycleId,
	payPeriodId,
	startDate,
	endDate,
	vehicleCategory,
}: IUseInventoryPaginatedQueryConfiguration) => {
	return useBasicPaginatedResource({
		queryKey: ["inventory", "vehicleInventory", locationId, startDate, endDate, vehicleCategory],
		queryFn: createQueryFunction({ locationId, commissionCycleId, payPeriodId, startDate, endDate, vehicleCategory }),
		getNextPageParam: (lastPage) => {
			const { pageInfo } = lastPage ?? {};
			return pageInfo?.hasNextPage ? { first: INVENTORY_PAGINATION_SIZE, after: pageInfo.endCursor ?? undefined } : undefined;
		},
		enabled: and(enabled, !!locationId, !!startDate, !!endDate, !!vehicleCategory),
		initialPageParam: { after: "", first: INVENTORY_PAGINATION_SIZE },
		fetchAllPages: true,
		staleTime: Infinity,
		gcTime: Infinity,
	});
};
